import { setGa4User } from '../lib/ga4';
import React, { createContext, useCallback, useContext, useState, useEffect, useRef} from "react";
import { meFn, refreshTokenRequest, getAssetsAvailability, Analytics, magicLinkLoginFn, trackConfirmedGa4Purchase } from '../lib/api';
import { clearSessionAuthorizationToken, setSessionAuthorizationToken } from '../lib/axiosInstance';
import { deleteCookie, setCookie, setPersistedValue, getPersistedValue } from '../lib/utils';
import config from '../lib/config';
import { clearMarketplaceCartCookie } from '../lib/marketplaceCart';
import { hasActivePremiumAssetsSubscription } from '../lib/subscriptionCapabilities';
// create context
const UserContext = createContext();
  
const UserContextProvider = ({ children }) => {

    const [user, setUser] = useState(null);
    const [userToken, setUserToken] = useState(false);
    const [refreshToken, setRefreshToken] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(true);
    const [focusMode, setFocusMode] = useState(false);
    const [assetsAvailable, setAssetsAvailable] = useState(0);
    const [isGettingAssetAvailability, setIsGettingAssetAvailability] = useState(true);
    const skipNextSessionLoadRef = useRef(false);


    useEffect(() => {
        let mounted = true;

        const cleanMagicLinkQueryParam = () => {
            const currentUrl = new URL(window.location.href);
            if (!currentUrl.searchParams.has('link_token')) {
                return;
            }
            currentUrl.searchParams.delete('link_token');
            const nextSearch = currentUrl.searchParams.toString();
            const nextUrl = `${currentUrl.pathname}${nextSearch ? `?${nextSearch}` : ''}${currentUrl.hash}`;
            window.history.replaceState({}, document.title, nextUrl);
        };

        const bootstrapTokenFromRequestOrStorage = (tokenRequestParameter) => {
            const token = getPersistedValue(
                config.sessionTokenLocalStorageKey,
                config.sessionTokenCookieKey,
                tokenRequestParameter
            );

            if(!token){
                clearSessionAuthorizationToken();
                setUserToken(null);
                setUser(null);
                setIsAuthenticating(false);
                return;
            }

            setSessionAuthorizationToken(token);
            setUserToken(token);
            setIsAuthenticating(true);
        };

        const loadSession = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const magicLinkToken = urlParams.get('link_token');
            const tokenRequestParameter = urlParams.get('u_t') ?? false;

            const refresh_token = getPersistedValue(config.refreshTokenLocalStorageKey, config.refreshTokenCookieKey);
            if(!refresh_token){
                setRefreshToken(null);
            } else {
                setRefreshToken(refresh_token);
            }

            if (magicLinkToken) {
                try {
                    const data = await magicLinkLoginFn(magicLinkToken);
                    if (!mounted) {
                        return;
                    }

                    if (data?.access_token) {
                        setSessionAuthorizationToken(data.access_token);
                        setUserToken(data.access_token);
                        setRefreshToken(data.refresh_token || null);
                        return;
                    }

                    bootstrapTokenFromRequestOrStorage(tokenRequestParameter);
                } catch (e) {
                    if (!mounted) {
                        return;
                    }
                    console.log("error while authenticating magic link", e);
                    bootstrapTokenFromRequestOrStorage(tokenRequestParameter);
                } finally {
                    cleanMagicLinkQueryParam();
                }
                return;
            }

            bootstrapTokenFromRequestOrStorage(tokenRequestParameter);
        };

        loadSession();

        window.refreshToken = async () => {
            const refresh_token = getPersistedValue(config.refreshTokenLocalStorageKey, config.refreshTokenCookieKey);

            if(refresh_token){
                let data;
                try{
                    data = await refreshTokenRequest(refresh_token);
                    if(data && data.access_token){
                        console.debug("Persist token and refresh after refres-token request response");
                        setSessionAuthorizationToken(data.access_token);
                        setPersistedValue(config.sessionTokenLocalStorageKey, config.sessionTokenCookieKey, data.access_token);
                        setPersistedValue(config.refreshTokenLocalStorageKey, config.refreshTokenCookieKey, data.refresh_token);
    
                        setUserToken(data.access_token);
                        setRefreshToken(data.refresh_token);
                        
                        //console.log("we got a refresh!");
                        return data.access_token;
                    }
                }catch(e){
                    console.log("error while refreshing token", e);
                }
                deleteCookie(config.refreshTokenCookieKey);

                //DEPRACATED LOCALSTORAGE
                //localStorage.removeItem(config.refreshTokenLocalStorageKey);

                return false;
            }else{
                return false;
                //await new Promise(resolve => setTimeout(resolve, 1000));
                //return await window.refreshToken();
            }
        };
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if(!userToken){
            return;
        }
        if (skipNextSessionLoadRef.current) {
            skipNextSessionLoadRef.current = false;
            return;
        }
        console.debug("use effect perssisting token");
        setSessionAuthorizationToken(userToken);
        setPersistedValue(config.sessionTokenLocalStorageKey, config.sessionTokenCookieKey, userToken);
        //delay in case refreshToken is being stored...
        setTimeout(loadCurrentUser, 300);  
    }, [userToken]);

    useEffect(() => {
        if(!refreshToken){
            return;
        } 
        console.debug("use effect perssisting refresh token");
        setPersistedValue(config.refreshTokenLocalStorageKey, config.refreshTokenCookieKey, refreshToken);
    }, [refreshToken]);
    


    useEffect(() => {
        console.log("USER", user);
        Analytics.setUserSettings(user?.settings ?? null);
        setGa4User(user);
        if (user && new URLSearchParams(window.location.search).has('ga4_purchase_ref')) trackConfirmedGa4Purchase();
        if(!user){
            return;
        }
        //console.log("USER", user);
        setIsAuthenticating(false);
        //Set a user creation tiemstamp as a cookie/localStorage
        if(user && user.created_at){
            let registrationTimestamp = new Date(user.created_at).getTime();
            
            //DEPRACATED LOCALSTORAGE
            //localStorage.setItem('crateRegistration', `${registrationTimestamp}`);
            setCookie('crateRegistration', `${registrationTimestamp}`, 365);
        }

        if (hasActivePremiumAssetsSubscription(user)) {
            setCookie(config.premiumAssetsSubscriptionCookieKey, '1', 365);
        } else {
            deleteCookie(config.premiumAssetsSubscriptionCookieKey);
        }

        //get assets availability
        getAssetsAvailability().then(data => {
            console.log("assets available", data);
            let remaining = 0;
            data.forEach(d => {
                if (d.name === 'assets-download') {
                    remaining = d.remaining;
                }
            });
            setAssetsAvailable(remaining);
            setIsGettingAssetAvailability(false);
        }).catch(err => {
            console.log("Error getting assets availability", err);
            setIsGettingAssetAvailability(false);
        });
    }, [user]);

    const loadCurrentUser = useCallback(async () => {
        setIsAuthenticating(true);
        try {
            let data = await meFn();
            data.premiumAssetsPolicy = false;
            data.isEnterprise = false;
            data.assetsPerDay = 0;

            const assetsFeature = data?.team?.subscription?.plan?.features?.find(feat => feat.name === 'assets-download');
            console.log({assetsFeature});
            if (assetsFeature) {
                data.assetsPerDay = +assetsFeature.charges;
                data.premiumAssetsPolicy = !assetsFeature.policy.name.toLowerCase().includes('free');
            }
            const usersOnTeam = data?.team?.subscription?.plan?.features?.find(feat => feat.name === 'users-on-team');
            if (usersOnTeam) {
                data.isEnterprise = usersOnTeam.charges > 1 ? usersOnTeam.charges : false;
            }
            setUser(data);
            return data;

        } catch(err) { 
            //console.log("User Authentication ERROR", err);
            clearSessionAuthorizationToken();
            setUserToken(null);
            setIsAuthenticating(false);
            return null;
        };
    }, []);

    const replaceUserSession = useCallback(async (accessToken, nextRefreshToken) => {
        clearMarketplaceCartCookie();
        setPersistedValue(config.sessionTokenLocalStorageKey, config.sessionTokenCookieKey, null);
        setPersistedValue(config.refreshTokenLocalStorageKey, config.refreshTokenCookieKey, null);
        setUser(null);
        setSessionAuthorizationToken(accessToken);
        setPersistedValue(config.sessionTokenLocalStorageKey, config.sessionTokenCookieKey, accessToken);
        setPersistedValue(config.refreshTokenLocalStorageKey, config.refreshTokenCookieKey, nextRefreshToken);
        skipNextSessionLoadRef.current = true;
        setUserToken(accessToken);
        setRefreshToken(nextRefreshToken);
        setIsAuthenticating(true);

        const nextUser = await loadCurrentUser();
        if (!nextUser) {
            throw new Error('Unable to load the signed-in account.');
        }

        return nextUser;
    }, [loadCurrentUser]);

    const unloadUser = () => {
        clearMarketplaceCartCookie();
        clearSessionAuthorizationToken();
        setPersistedValue(config.sessionTokenLocalStorageKey, config.sessionTokenCookieKey, null);
        setPersistedValue(config.refreshTokenLocalStorageKey, config.refreshTokenCookieKey, null);
        setUserToken(null);
        setRefreshToken(null);
        setUser(null);
        setIsAuthenticating(false);
    };

    return (
        // the Provider gives access to the context to its children
        <UserContext.Provider
          value={{
            user,
            userToken,
            isAuthenticating,
            setUser,
            setUserToken,
            setRefreshToken,
            reloadCurrentUser: loadCurrentUser,
            replaceUserSession,
            unloadUser,
            focusMode,
            setFocusMode,
            assetsAvailable,
            isGettingAssetAvailability,
          }}
        >
          {children}
        </UserContext.Provider>
      );
};

const useUserContext = () => useContext(UserContext);

export { useUserContext, UserContextProvider };
